import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star, Trash2, Award, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const JURY_CRITERIA = [
  { key: 'hospitality',  label: 'Exceptional Hospitality',                                        weight: 0.30 },
  { key: 'satisfaction', label: 'Outstanding guest satisfaction & service consistency',            weight: 0.25 },
  { key: 'facilities',   label: 'Clean, safe, and well-maintained facilities',                     weight: 0.20 },
  { key: 'community',    label: 'Stronger community and sustainability engagement',                weight: 0.15 },
  { key: 'fnb',          label: 'Enhanced food & beverage offering',                               weight: 0.10 },
];

function tierLabel(t) {
  if (!t) return null;
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/**
 * Renders the rating UI appropriate for the current user's role.
 *  - public  → 5-star
 *  - partner → 1-10
 *  - jury    → 5-criteria 0-100 sliders
 *  - admin   → single 0-100 compliance score
 * Calls onScoresUpdate(scores) after every save/delete so the parent can refresh.
 */
export default function RatingPanel({ awardId, user, onScoresUpdate }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [existing, setExisting] = useState(null);

  // form state per role
  const [stars, setStars]     = useState(0);
  const [score10, setScore10] = useState('');
  const [criteria, setCriteria] = useState({
    hospitality: 0, satisfaction: 0, facilities: 0, community: 0, fnb: 0,
  });
  const [adminScore, setAdminScore] = useState('');

  const role = user?.role;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${API_URL}/api/awards/${awardId}/rating`, { withCredentials: true })
      .then((res) => {
        if (!alive) return;
        const r = res.data?.rating;
        setExisting(r || null);
        if (r?.raw) {
          if (r.source === 'public' && r.raw.stars) setStars(r.raw.stars);
          if (r.source === 'partner' && r.raw.score_10 != null) setScore10(String(r.raw.score_10));
          if (r.source === 'jury' && r.raw.criteria) setCriteria(r.raw.criteria);
          if (r.source === 'admin' && r.raw.score != null) setAdminScore(String(r.raw.score));
        }
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [awardId, role]);

  if (!user) return null;
  if (!role || !['public', 'partner', 'professional_partner', 'jury', 'admin'].includes(role)) return null;

  // Legacy Partner role has no voting access
  if (role === 'partner') {
    return (
      <div className="bg-[#0F0F0F] border border-white/10 rounded-2xl p-6 sm:p-8 text-center" data-testid="rating-panel-partner-readonly">
        <Award className="w-6 h-6 text-[#D4AF37]/70 mx-auto mb-3" />
        <p className="font-['Playfair_Display'] text-xl text-white mb-2">
          Partner Account
        </p>
        <p className="text-white/60 text-sm max-w-md mx-auto">
          Your Partner account is for managing listings and your brand profile.
          Voting access is reserved for <span className="text-[#D4AF37]">Professional Partners</span>.
          Contact the administrator to upgrade your account if you wish to participate in the rating process.
        </p>
      </div>
    );
  }

  const submit = async (endpoint, body) => {
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}${endpoint}`, body, { withCredentials: true });
      toast.success('Rating saved');
      onScoresUpdate?.(res.data);
      setExisting({ source: role, raw: body, value_0_100: res.data?.[`score_${role}`] });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save rating');
    } finally {
      setSaving(false);
    }
  };

  const removeRating = async () => {
    if (!existing) return;
    setSaving(true);
    try {
      const res = await axios.delete(`${API_URL}/api/awards/${awardId}/rate`, { withCredentials: true });
      toast.success('Rating removed');
      onScoresUpdate?.(res.data);
      setExisting(null);
      setStars(0); setScore10(''); setAdminScore('');
      setCriteria({ hospitality: 0, satisfaction: 0, facilities: 0, community: 0, fnb: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove rating');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="h-32 skeleton rounded-xl" />;
  }

  // ===== PUBLIC: 5 stars =====
  if (role === 'public') {
    const submitPublic = () => {
      if (!stars) { toast.error('Please select 1-5 stars'); return; }
      submit(`/api/awards/${awardId}/rate/public`, { stars });
    };
    return (
      <RoleCard
        title="Your Rating"
        subtitle="Public voting · 15% weight"
        icon={<Star className="w-5 h-5 text-[#D4AF37]" />}
        existing={existing}
        onRemove={removeRating}
        saving={saving}
        weight="15%"
      >
        <div className="flex items-center gap-2 mb-6" data-testid="public-stars">
          {[1,2,3,4,5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setStars(n)}
              className="transition-transform hover:scale-110"
              data-testid={`star-${n}`}
              aria-label={`${n} stars`}
            >
              <Star className={`w-9 h-9 ${n <= stars ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-white/20'}`} />
            </button>
          ))}
          {stars > 0 && (
            <span className="ml-3 text-sm text-white/60">
              {stars} / 5 → normalized to {(stars * 20).toFixed(0)}/100
            </span>
          )}
        </div>
        <Button onClick={submitPublic} disabled={saving || !stars} className="bg-[#D4AF37] hover:bg-[#E5C048] text-[#0A0A0A] h-11" data-testid="submit-public-rating">
          {existing ? 'Update Rating' : 'Submit Rating'}
        </Button>
      </RoleCard>
    );
  }

  // ===== PROFESSIONAL PARTNER: 1-10 =====
  if (role === 'professional_partner') {
    const n = parseFloat(score10);
    const submitPartner = () => {
      if (!n || n < 1 || n > 10) { toast.error('Score must be 1-10'); return; }
      submit(`/api/awards/${awardId}/rate/partner`, { score_10: n });
    };
    return (
      <RoleCard
        title="Professional Partner Rating"
        subtitle="Professional partners · 20% weight"
        icon={<Award className="w-5 h-5 text-[#D4AF37]" />}
        existing={existing}
        onRemove={removeRating}
        saving={saving}
        weight="20%"
      >
        <div className="flex items-center gap-3 mb-3">
          <input
            type="range" min="1" max="10" step="0.5"
            value={n || 1}
            onChange={(e) => setScore10(e.target.value)}
            className="flex-1 accent-[#D4AF37]"
            data-testid="partner-slider"
          />
          <Input
            type="number" min="1" max="10" step="0.5"
            value={score10}
            onChange={(e) => setScore10(e.target.value)}
            placeholder="0-10"
            className="w-24 bg-[#0A0A0A] border-white/10 text-white"
            data-testid="partner-input"
          />
        </div>
        {n > 0 && (
          <p className="text-sm text-white/60 mb-6">
            {n.toFixed(1)} / 10 → normalized to {(n * 10).toFixed(0)}/100
          </p>
        )}
        <Button onClick={submitPartner} disabled={saving} className="bg-[#D4AF37] hover:bg-[#E5C048] text-[#0A0A0A] h-11" data-testid="submit-partner-rating">
          {existing ? 'Update Rating' : 'Submit Rating'}
        </Button>
      </RoleCard>
    );
  }

  // ===== JURY: 5 criteria =====
  if (role === 'jury') {
    const totalJury = JURY_CRITERIA.reduce(
      (acc, c) => acc + (parseFloat(criteria[c.key]) || 0) * c.weight, 0
    );
    const submitJury = () => {
      for (const c of JURY_CRITERIA) {
        const v = parseFloat(criteria[c.key]);
        if (isNaN(v) || v < 0 || v > 100) {
          toast.error(`${c.label} must be 0-100`); return;
        }
      }
      submit(`/api/awards/${awardId}/rate/jury`, criteria);
    };
    return (
      <RoleCard
        title="Jury Scoring"
        subtitle="Independent jury · 30% weight"
        icon={<Award className="w-5 h-5 text-[#D4AF37]" />}
        existing={existing}
        onRemove={removeRating}
        saving={saving}
        weight="30%"
      >
        <div className="space-y-5 mb-6">
          {JURY_CRITERIA.map((c) => {
            const v = parseFloat(criteria[c.key]) || 0;
            return (
              <div key={c.key} data-testid={`jury-${c.key}`}>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm text-white/80">{c.label}</label>
                  <span className="text-xs text-[#D4AF37]/80 tracking-wider">
                    {(c.weight * 100).toFixed(0)}%
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="range" min="0" max="100" step="1"
                    value={v}
                    onChange={(e) => setCriteria({ ...criteria, [c.key]: parseFloat(e.target.value) })}
                    className="flex-1 accent-[#D4AF37]"
                  />
                  <Input
                    type="number" min="0" max="100"
                    value={criteria[c.key]}
                    onChange={(e) => setCriteria({ ...criteria, [c.key]: e.target.value })}
                    className="w-20 bg-[#0A0A0A] border-white/10 text-white"
                  />
                </div>
              </div>
            );
          })}
        </div>
        <div className="p-4 rounded-lg bg-[#0A0A0A] border border-[#D4AF37]/20 mb-6 flex items-center justify-between">
          <span className="text-sm text-white/70">Composite jury score</span>
          <span className="text-2xl font-['Playfair_Display'] text-[#D4AF37]" data-testid="jury-total">
            {totalJury.toFixed(1)} / 100
          </span>
        </div>
        <Button onClick={submitJury} disabled={saving} className="bg-[#D4AF37] hover:bg-[#E5C048] text-[#0A0A0A] h-11" data-testid="submit-jury-rating">
          {existing ? 'Update Score' : 'Submit Score'}
        </Button>
      </RoleCard>
    );
  }

  // ===== ADMIN: 0-100 compliance =====
  if (role === 'admin') {
    const n = parseFloat(adminScore);
    const submitAdmin = () => {
      if (isNaN(n) || n < 0 || n > 100) { toast.error('Score must be 0-100'); return; }
      submit(`/api/awards/${awardId}/rate/admin`, { score: n });
    };
    return (
      <RoleCard
        title="Travel-Awards.Org"
        subtitle="Administrative review · 35% weight"
        icon={<Shield className="w-5 h-5 text-[#D4AF37]" />}
        existing={existing}
        onRemove={removeRating}
        saving={saving}
        weight="35%"
      >
        <div className="flex items-center gap-3 mb-3">
          <input
            type="range" min="0" max="100" step="1"
            value={n || 0}
            onChange={(e) => setAdminScore(e.target.value)}
            className="flex-1 accent-[#D4AF37]"
            data-testid="admin-slider"
          />
          <Input
            type="number" min="0" max="100"
            value={adminScore}
            onChange={(e) => setAdminScore(e.target.value)}
            placeholder="0-100"
            className="w-24 bg-[#0A0A0A] border-white/10 text-white"
            data-testid="admin-input"
          />
        </div>
        {!isNaN(n) && (
          <p className="text-sm text-white/60 mb-6">Compliance score: {n}/100</p>
        )}
        <Button onClick={submitAdmin} disabled={saving} className="bg-[#D4AF37] hover:bg-[#E5C048] text-[#0A0A0A] h-11" data-testid="submit-admin-rating">
          {existing ? 'Update Score' : 'Submit Score'}
        </Button>
      </RoleCard>
    );
  }

  return null;
}

function RoleCard({ title, subtitle, icon, weight, existing, onRemove, saving, children }) {
  return (
    <div className="bg-[#0F0F0F] border border-white/10 rounded-2xl p-6 sm:p-8" data-testid="rating-panel">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-start gap-3">
          {icon}
          <div>
            <h3 className="font-['Playfair_Display'] text-xl text-white">{title}</h3>
            <p className="text-xs text-white/50 uppercase tracking-[0.2em] mt-1">{subtitle}</p>
          </div>
        </div>
        {existing && (
          <button
            onClick={onRemove}
            disabled={saving}
            className="text-white/40 hover:text-red-400 transition-colors text-sm flex items-center gap-1"
            data-testid="remove-rating-btn"
          >
            <Trash2 className="w-4 h-4" />
            Remove
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

/** Public score display — shows final score + tier badge, plus full breakdown for privileged roles. */
export function ScoreBreakdown({ scores, role }) {
  if (!scores || scores.final_score == null) {
    return (
      <div className="p-5 rounded-xl border border-dashed border-white/10 text-center">
        <p className="text-white/40 text-sm">Pending ratings — no final score yet.</p>
      </div>
    );
  }
  const tier = scores.auto_tier;
  const tierColors = {
    gold:    'bg-[#D4AF37] text-[#0A0A0A]',
    silver:  'bg-[#C0C0C0] text-[#0A0A0A]',
    bronze:  'bg-[#CD7F32] text-white',
    nominee: 'bg-white/10 text-white/80',
  };
  const showBreakdown = ['admin', 'jury', 'partner'].includes(role);
  return (
    <div className="bg-[#0F0F0F] border border-white/10 rounded-2xl p-6 sm:p-8" data-testid="score-breakdown">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs text-white/50 uppercase tracking-[0.2em]">Final Score</p>
          <p className="font-['Playfair_Display'] text-5xl text-[#D4AF37] mt-1" data-testid="final-score">
            {scores.final_score.toFixed(1)}
            <span className="text-2xl text-white/40 ml-1">/ 100</span>
          </p>
        </div>
        {tier && (
          <span
            className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.2em] ${tierColors[tier] || tierColors.nominee}`}
            data-testid="auto-tier-badge"
          >
            {tierLabel(tier)}
          </span>
        )}
      </div>

      {showBreakdown && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          {[
            { key: 'public',  label: 'Public',  w: '15%' },
            { key: 'partner', label: 'Partner', w: '20%' },
            { key: 'jury',    label: 'Jury',    w: '30%' },
            { key: 'admin',   label: 'Admin',   w: '35%' },
          ].map((s) => {
            const v = scores[`score_${s.key}`];
            return (
              <div key={s.key} className="p-3 rounded-lg bg-[#0A0A0A] border border-white/5" data-testid={`sub-score-${s.key}`}>
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">
                  {s.label} · {s.w}
                </p>
                <p className="font-['Playfair_Display'] text-xl text-white">
                  {v == null ? '—' : v.toFixed(1)}
                </p>
                <p className="text-[10px] text-white/40">
                  {(scores.rating_counts?.[s.key] ?? 0)} rating{(scores.rating_counts?.[s.key] ?? 0) === 1 ? '' : 's'}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
